import { shrinkImage } from './image';
import type { AlbumCard, Friend, Postcard, Series, Stamp, User } from './types';

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: init.body instanceof FormData
      ? {}
      : { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });

  if (res.status === 204) return undefined as T;

  let body: unknown = null;
  if ((res.headers.get('Content-Type') ?? '').includes('application/json')) {
    body = await res.json().catch(() => null);
  }

  if (!res.ok) {
    const msg =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `error ${res.status}`;
    throw new ApiError(msg, res.status);
  }
  return body as T;
}

export const api = {
  me: () => request<{ user: User }>('/api/me'),

  updateMe: (patch: { handle: string; country: string; lang: string; avatarMedia?: string | null; countryChosen?: boolean }) =>
    request<{ user: User }>('/api/me', {
      method: 'PATCH',
      body: JSON.stringify({ avatarMedia: null, ...patch }),
    }),

  mailbox: () => request<{ cards: Postcard[] }>('/api/mailbox'),

  // Only the caller's own cards: nothing on its way to them is ever listed.
  transit: () => request<{ outgoing: Postcard[]; now: string }>('/api/transit'),

  send: (card: {
    recipient: string;
    frontMedia: string | null;
    frontLabel: string;
    noteText: string;
    noteMedia: string | null;
    stamp: string;
  }) => request<{ card: Postcard }>('/api/postcards', { method: 'POST', body: JSON.stringify(card) }),

  markRead: (id: string) => request<{ ok: boolean }>(`/api/postcards/${id}/read`, { method: 'POST' }),

  cancel: (id: string) => request<{ ok: boolean }>(`/api/postcards/${id}/cancel`, { method: 'POST' }),

  friends: () => request<{ friends: Friend[] }>('/api/friends'),

  addFriend: (handle: string) =>
    request<{ friend: Friend }>('/api/friends', {
      method: 'POST',
      body: JSON.stringify({ handle }),
    }),

  stamps: (deviceCountry: string) =>
    request<{ stamps: Stamp[]; country: string; series: Series[]; all: Stamp[] }>(
      `/api/stamps?country=${encodeURIComponent(deviceCountry)}`,
    ),

  album: () => request<{ album: AlbumCard[] }>('/api/album'),

  upload: async (file: File) => {
    const form = new FormData();
    form.append('file', await shrinkImage(file), file.name);
    return request<{ id: string; mime: string }>('/api/media', { method: 'POST', body: form });
  },

  pushKey: () => request<{ publicKey: string }>('/api/push'),

  pushSubscribe: (sub: PushSubscriptionJSON) =>
    request<{ ok: boolean }>('/api/push/subscribe', { method: 'POST', body: JSON.stringify(sub) }),

  pushUnsubscribe: (endpoint: string) =>
    request<{ ok: boolean }>('/api/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),

  logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  // Which ways in the sign-in page should offer. devUsers is only ever
  // non-empty on a local development server.
  authOptions: () => request<{ google: boolean; devUsers: string[] }>('/auth/options'),

  devLogin: (handle: string) =>
    request<{ user: User }>('/auth/dev/login', { method: 'POST', body: JSON.stringify({ handle }) }),
};

export const mediaUrl = (id: string): string => `/api/media/${id}`;
